import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Clock, ArrowLeft, ChevronRight } from "lucide-react";
import { setPageMeta, injectJsonLd, removeJsonLd } from "@/lib/seo";
import { articleSchema } from "@/lib/schema";
import { ShareMenu } from "@/components/ShareMenu";
import { trackEvent } from "@/lib/analytics";
import AdSlot from "@/components/AdSlot";
import { AD_SLOTS } from "@/config/adSlots";

// Renderizza `**testo**` come <strong>. Usato in widget e strategie.
// Split naive che alterna testo semplice e segmenti in grassetto.
function renderInlineBold(text) {
  if (!text) return text;
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    const m = part.match(/^\*\*([^*]+)\*\*$/);
    return m ? <strong key={i} className="text-[#1A362D] font-semibold">{m[1]}</strong> : part;
  });
}

export default function ArticleDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api.get(`/articles/${slug}`)
      .then(({ data }) => setArticle(data))
      .catch((e) => {
        if (e?.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  // SEO meta + JSON-LD (invisibili all'utente)
  useEffect(() => {
    if (!article) return;
    const description = (article.trigger || article.title || "").slice(0, 250);
    setPageMeta({
      title: `${article.title} — Papà dito · Per manina`,
      description,
      canonicalPath: `/blog/${article.slug}`,
      ogType: "article",
      ogImage: article.image_url || undefined,
    });
    injectJsonLd("schema-article", articleSchema(article));
    return () => removeJsonLd("schema-article");
  }, [article]);

  // Conversion event: user has read the article for at least 45 seconds (engagement signal).
  // Fires once per article view. Used by Google Ads Smart Bidding to identify quality traffic.
  useEffect(() => {
    if (!article) return;
    const t = setTimeout(() => {
      trackEvent("blog_read", { article_slug: article.slug, article_title: article.title });
    }, 45000);
    return () => clearTimeout(t);
  }, [article]);

  if (loading) {
    return <div className="max-w-3xl mx-auto px-4 py-20 font-ui text-[#5C6B64]" data-testid="article-loading">Caricamento…</div>;
  }
  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="article-notfound">
        <h1 className="font-display text-3xl text-[#1A362D]">Articolo non trovato</h1>
        <Link to="/blog" className="inline-block mt-6 btn-secondary text-sm">Torna al blog</Link>
      </div>
    );
  }
  if (!article) return null;

  return (
    <article className="bg-[#FAF9F6]" data-testid="article-page">
      {/* Header */}
      <header className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-8">
        <Link to="/blog" className="inline-flex items-center gap-1.5 font-ui text-xs text-[#5C6B64] hover:text-[#C05C45] transition-colors" data-testid="back-to-blog-link">
          <ArrowLeft size={14} /> Tutti gli articoli
        </Link>
        <p className="mt-6 label-eyebrow text-[#C05C45]">Papà dito · Il blog breve</p>
        <h1 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl text-[#1A362D] tracking-tight leading-tight" data-testid="article-title">
          {article.title}
        </h1>
        <div className="mt-5 flex flex-wrap items-center gap-3">
          {(article.tags || []).map((t) => (
            <span key={t} className="inline-block px-2.5 py-0.5 rounded-full bg-[#FAF3E7] border border-[#E8DCC4] font-ui text-xs text-[#5C6B64]">
              {t}
            </span>
          ))}
          <span className="inline-flex items-center gap-1 font-ui text-xs text-[#8A9691]">
            <Clock size={12} /> {article.reading_time_min} min di lettura
          </span>
          <div className="ml-auto">
            <ShareMenu type="blog" slug={article.slug} title={article.title} />
          </div>
        </div>
      </header>

      {/* Hero image */}
      {article.image_url && (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mb-10">
          <div className="relative aspect-[16/9] sm:aspect-[2/1] rounded-xl overflow-hidden bg-[#DCE4DC]">
            <img
              src={article.image_url}
              alt={article.title}
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="article-hero-image"
            />
          </div>
        </div>
      )}

      {/* Body */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 space-y-10 font-ui text-[#1A362D]">

        {/* Trigger */}
        <section data-testid="article-trigger">
          <p className="label-eyebrow text-[#C05C45]">Il trigger</p>
          <p className="mt-3 text-base sm:text-lg leading-relaxed text-[#1A362D]">
            {article.trigger}
          </p>
        </section>

        {/* Perché */}
        <section data-testid="article-perche">
          <p className="label-eyebrow text-[#C05C45]">Il perché</p>
          <p className="mt-3 text-base sm:text-lg leading-relaxed text-[#1A362D]">
            {article.perche}
          </p>
        </section>

        {/* Strategie pratiche */}
        <section data-testid="article-strategie">
          <p className="label-eyebrow text-[#C05C45]">La strategia pratica</p>
          <div className="mt-4 space-y-5">
            {(article.strategie || []).map((s, idx) => (
              <div key={idx} className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#1A362D] text-[#FAF9F6] font-display flex items-center justify-center text-sm">
                  {idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-display text-lg text-[#1A362D]">{s.titolo}</h3>
                  <p className="mt-1.5 text-base leading-relaxed text-[#5C6B64] whitespace-pre-line">{renderInlineBold(s.testo)}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Widget */}
        {article.widget && (
          <aside className="card-surface p-6 sm:p-8 bg-[#FAF3E7] border-[#E8DCC4]" data-testid="article-widget">
            <p className="label-eyebrow text-[#C05C45]">{article.widget.titolo}</p>
            <div className="mt-4 flex items-start gap-4">
              <span className="text-3xl" aria-hidden>{article.widget.emoji}</span>
              <p className="flex-1 text-base leading-relaxed text-[#1A362D]">{renderInlineBold(article.widget.testo)}</p>
            </div>
            <Link
              to={article.widget.cta_href || "/prodotti"}
              className="mt-5 inline-flex items-center gap-1.5 font-ui text-sm font-medium text-[#C05C45] hover:gap-2.5 transition-all"
              data-testid="article-products-link"
            >
              {article.widget.cta_text || "Esplora la nostra selezione di libri e giochi"} <ChevronRight size={15} />
            </Link>
          </aside>
        )}

        {/* Share CTA — "Ti è stato utile? Condividi con un altro genitore" */}
        <section className="mt-10 card-surface p-6 sm:p-8 bg-[#F2EFEB] border-[#DCE4DC] text-center" data-testid="article-share-cta">
          <p className="label-eyebrow text-[#C05C45]">Ti è stato utile?</p>
          <p className="mt-2 font-display text-2xl sm:text-3xl text-[#1A362D] leading-tight">
            Condividilo con un altro genitore.
          </p>
          <p className="mt-2 font-ui text-sm text-[#5C6B64] max-w-md mx-auto">
            Nessuno insegna a fare il genitore: costruiamo insieme una piccola cassetta degli attrezzi.
          </p>
          <div className="mt-5 inline-block">
            <ShareMenu type="blog" slug={article.slug} title={article.title} />
          </div>
        </section>

        {/* AdSense end-of-article (si attiva solo quando AD_SLOTS.BLOG_END_OF_ARTICLE è compilato) */}
        <AdSlot
          slot={AD_SLOTS.BLOG_END_OF_ARTICLE}
          format="auto"
          className="mt-10"
          testId="adslot-blog-end-of-article"
        />

        {/* CTA bottom */}
        <section className="divider pt-10 text-center">
          <p className="font-ui text-sm text-[#5C6B64]">Ti è piaciuto questo articolo?</p>
          <Link to="/blog" className="mt-3 inline-flex items-center gap-1.5 font-ui text-sm font-medium text-[#C05C45] hover:underline" data-testid="article-back-list">
            <ArrowLeft size={14} /> Continua a leggere Papà dito
          </Link>
        </section>
      </div>
    </article>
  );
}
